import { useMutation, useQuery } from '@apollo/react-hooks';
import { createStyles, Grid, makeStyles, Box } from '@material-ui/core';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import {
	AlertBox,
	Button, ExtendedTheme, Loader, Option, Progress, Step, StepContent, StepLabel, Stepper
} from 'neon-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { ISO_COUNTRIES, STRIPE_TAX_COUNTRIES } from '../../../gql/countries';
import { CreateStripeCustomerInput, CreateStripeSubscriptionInput, CreateTaxIdResult, CREATE_STRIPE_CUSTOMER, CREATE_STRIPE_SUBSCRIPTION, StripePendingSubscription } from '../../../gql/subscriptions';
import { IsoCountry, toCountryIsoCodeMap } from '../../../reference-data/country-iso-codes';
import { getCountryFromValue, stripeTaxCountriesOptions, StripeTaxCountry, StripeTaxCountryInfo } from '../../../reference-data/stripeSupportedTaxIds';
import { useFormControl } from '../../../utils/form.utils';
import {booleanTrueValidator, requiredValidator} from '../../../utils/validator.utils';
import { StripeBusinessAddress } from './StripeBusinessAddress';
import { StripePaymentForm } from './StripePaymentElement';
import { StripeTaxId } from './StripeTaxId';
import TermsAndConditionsCheckbox from '../../ui/TermsAndConditionsCheckbox';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) =>
	createStyles({
		stepperRoot: {
			padding: 0,
			width: '100%'
		},
		element: {
			padding: theme.spacing(0)
		},
		button: {
			marginLeft: theme.spacing(1)
		},
		loaderPaper: {
			minHeight: 160,
			padding: theme.spacing(2),
			justifyContent: 'center',
			alignItems: 'center'
		},
		loaderItems: {
			gap: theme.spacing(2)
		},
		buttonSpacing: {
			display: 'flex',
			gap: theme.spacing(1)
		}
	})
);

export interface StripePaymentStepperProps {
	channelId: string;
	successUrl: string;
	goBack: () => void;
}

export const StripePaymentStepper = ({
	channelId,
	successUrl,
	goBack
}: StripePaymentStepperProps) => {
	const classes = useStyles();
	const [stripeError, setStripeError] = useState<string | undefined>();
	const [activeStep, setActiveStep] = React.useState(0);
	const [businessIsoCountryCode, setBusinessIsoCountryCode] = useState<string>();
	const [stripeTaxCountryInfo, setStripeTaxCountryInfo] = useState<StripeTaxCountryInfo>();
	const [isValidTaxId, setIsValidTaxId] = useState<boolean>(true);
	const [isValidBillingAddress, setIsValidBillingAddress] = useState<boolean>(true);
	const [taxIdResult, setTaxIdResult] = useState<CreateTaxIdResult>(CreateTaxIdResult.NotSet);
	const [stripe, setStripe] = useState<Stripe | null>();
	const [elements, setElements] = useState<StripeElements | null>();
	const [countryOptions, setCountryOptions] = useState<Option[]>([]);
	const [countryIsoCodeMap, setCountryIsoCodeMap] = useState<Map<string, string>>(new Map<string, string>([]));
	const [paymentLoading, setPaymentLoading] = useState(false);
	const businessNameFormControl = useFormControl<string>('', [requiredValidator]);
	const businessCountryFormControl = useFormControl<Option | undefined>(undefined, [requiredValidator]);
	const taxIdFormControl = useFormControl<string>('', [requiredValidator]);
	const addressLine1FormControl = useFormControl<string>('', [requiredValidator]);
	const addressLine2FormControl = useFormControl<string>('');
	const addressStateFormControl = useFormControl<string>('');
	const addressCityFormControl = useFormControl<string>('', [requiredValidator]);
	const addressPostcodeFormControl = useFormControl<string>('', [requiredValidator]);
	const acceptedTermsFormControl = useFormControl<boolean>(false, [booleanTrueValidator])

	const { data: stripeTaxCountriesResponse, loading: stripeTaxCountriesLoading } =
		useQuery<{ stripeTaxCountries: StripeTaxCountry[] }>(STRIPE_TAX_COUNTRIES);

	const { data: stripeIsoCountriesResponse, loading: stripeIsoCountriesLoading } =
		useQuery<{ isoCountries: IsoCountry[] }>(ISO_COUNTRIES);

	const [createStripeSubscription, { data: stripeSubscriptionData, loading: createStripeSubscriptionLoading }] =
		useMutation<StripePendingSubscription, { item: CreateStripeSubscriptionInput }>(CREATE_STRIPE_SUBSCRIPTION, {
			onError: error => {
				console.log(error)
			}
		});

	const [createStripeCustomer, { loading: createStripeCustomerLoading }] =
		useMutation<{ createStripeCustomer: CreateTaxIdResult }, { item: CreateStripeCustomerInput }>(CREATE_STRIPE_CUSTOMER, { onError: console.log });

	const taxIdValidator = (createTaxIdResult: CreateTaxIdResult): boolean => {
		switch (createTaxIdResult) {
		case CreateTaxIdResult.Success:
		case CreateTaxIdResult.TaxIdAlreadyExists:
		case CreateTaxIdResult.NotSet:
			return true;
		case CreateTaxIdResult.MaxTaxIDError:
		case CreateTaxIdResult.TaxIdInvalidFormat:
		case CreateTaxIdResult.UnknownError:
		default:
			return false;
		}
	}

	const parseCreateTaxIdError = (createTaxIdResult: CreateTaxIdResult): string => {
		switch (createTaxIdResult) {
		case CreateTaxIdResult.NotSet:
		case CreateTaxIdResult.Success:
		case CreateTaxIdResult.TaxIdAlreadyExists:
			return '';
		case CreateTaxIdResult.TaxIdInvalidFormat:
		case CreateTaxIdResult.UnknownError:
			return 'Invalid Tax ID.';
		case CreateTaxIdResult.MaxTaxIDError:
			return 'We only support up to 5 tax IDs per user.';
		}
	}

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const taxValidator = useCallback(() => {
		return {
			valid: isValidTaxId,
			message: parseCreateTaxIdError(taxIdResult)
		}
	}, [isValidTaxId]);

	const billingAddressValidator = useCallback(() => {
		return {
			valid: isValidBillingAddress,
			message: 'Please enter a valid postcode'
		}
	}, [isValidBillingAddress]);

	useEffect(() => {
		if (!stripeTaxCountriesResponse || !stripeTaxCountriesResponse.stripeTaxCountries) return;

		const countryOptions = stripeTaxCountriesOptions(stripeTaxCountriesResponse.stripeTaxCountries);
		setCountryOptions(countryOptions);
	}, [stripeTaxCountriesResponse]);

	useEffect(() => {
		if (!stripeIsoCountriesResponse || !stripeIsoCountriesResponse.isoCountries) return;

		const isoCountries = toCountryIsoCodeMap(stripeIsoCountriesResponse.isoCountries);
		setCountryIsoCodeMap(isoCountries);
	}, [stripeIsoCountriesResponse]);

	useEffect(() => {
		if (!isValidTaxId) {
			taxIdFormControl.setValidators([taxValidator, requiredValidator]);
		}
	}, [isValidTaxId, stripeSubscriptionData, taxValidator]);

	useEffect(() => {
		if (!isValidBillingAddress) {
			addressPostcodeFormControl.setValidators([billingAddressValidator, requiredValidator]);
		}
	}, [isValidBillingAddress, stripeSubscriptionData, billingAddressValidator])

	const removeTaxIdErrorMessage = () => {
		setIsValidTaxId(true);
		taxIdFormControl.setValidators([requiredValidator]);
	}

	const removeBillingAddressErrorMessage = () => {
		setIsValidBillingAddress(true);
		addressPostcodeFormControl.setValidators([requiredValidator]);
	}

	const submitTaxDetailsButtonClick = async () => {
		if (!channelId) return;

		if (!businessNameFormControl.isValid ||
			!businessCountryFormControl.isValid ||
			!taxIdFormControl.isValid) {
			businessNameFormControl.setDirty(true)
			businessCountryFormControl.setDirty(true)
			taxIdFormControl.setDirty(true)
			return;
		}

		const countryControlValue = businessCountryFormControl.value?.value.toString();
		const parsedStripeTaxCountry: StripeTaxCountryInfo = getCountryFromValue(countryControlValue ?? '');
		setStripeTaxCountryInfo(parsedStripeTaxCountry);
		const isoCountryCode = countryIsoCodeMap.get(parsedStripeTaxCountry.name);
		setBusinessIsoCountryCode(isoCountryCode);

		const item = {
			channelId: channelId,
			businessName: businessNameFormControl.value,
			taxId: taxIdFormControl.value,
			taxIdType: parsedStripeTaxCountry.code,
			addressLine1: addressLine1FormControl.value,
			addressLine2: addressLine2FormControl.value,
			addressState: addressStateFormControl.value,
			addressCity: addressCityFormControl.value,
			addressPostcode: addressPostcodeFormControl.value,
			addressCountryCode: isoCountryCode ?? ''
		};

		const createStripeCustomerResponse = await createStripeCustomer({ variables: { item: item } });
		const createTaxIdResult = createStripeCustomerResponse.data?.createStripeCustomer ?? CreateTaxIdResult.NotSet;
		setTaxIdResult(createTaxIdResult);
		const parseTaxIdValidationResult = taxIdValidator(createTaxIdResult);
		setIsValidTaxId(parseTaxIdValidationResult);
		parseTaxIdValidationResult && setActiveStep((prevActiveStep) => prevActiveStep + 1);
	}

	const submitBillingInfoClick = async () => {
		if (addressLine1FormControl.isValid &&
			addressLine2FormControl.isValid &&
			addressStateFormControl.isValid &&
			addressCityFormControl.isValid &&
			addressPostcodeFormControl.isValid) {
			const item = {
				channelId: channelId,
				businessName: businessNameFormControl.value,
				taxId: taxIdFormControl.value,
				addressLine1: addressLine1FormControl.value,
				addressLine2: addressLine2FormControl.value,
				addressState: addressStateFormControl.value,
				addressCity: addressCityFormControl.value,
				addressPostcode: addressPostcodeFormControl.value,
				addressCountryCode: businessIsoCountryCode ?? ''
			};

			const createStripeSubscriptionResponse = await createStripeSubscription({ variables: { item: item } });
			const subscriptionAddressIsValid = createStripeSubscriptionResponse.data?.createStripeSubscription.addressIsValid || false;
			setIsValidBillingAddress(subscriptionAddressIsValid);

			if (subscriptionAddressIsValid) {
				setActiveStep((prevActiveStep) => prevActiveStep + 1);
				return;
			}
		}

		addressLine1FormControl.setDirty(true)
		addressLine2FormControl.setDirty(true);
		addressStateFormControl.setDirty(true);
		addressCityFormControl.setDirty(true);
		addressPostcodeFormControl.setDirty(true);
	}

	const createStripeSubscriptionButtonClick = async () => {
		if (!channelId) return;
		if (!stripe || !elements) return;
		if (!stripeSubscriptionData) return;
		if (!acceptedTermsFormControl.value) {
			acceptedTermsFormControl.setDirty(true);
			return;
		}

		setPaymentLoading(true);

		const { error } = await stripe.confirmPayment({
			elements: elements as any,
			confirmParams: {
				/* eslint-disable  */
				return_url: `${window.location.origin}/insights${successUrl}`,

				payment_method_data: {
					billing_details: {
						address: {
							city: addressCityFormControl.value,
							line1: addressLine1FormControl.value,
							line2: addressLine2FormControl.value,
							state: addressStateFormControl.value,
							country: businessIsoCountryCode,
							postal_code: addressPostcodeFormControl.value
						}
					}
				}
				/* eslint-enable  */
			}
		});

		if (error) {
			setStripeError(error?.message);
			setPaymentLoading(false);
			return;
		}
	}

	const onElementsReady = (stripe: Stripe | null, elements: StripeElements | null) => {
		setStripe(stripe);
		setElements(elements);
	}

	const steps = () => {
		return [
			{
				label: 'Business and tax identification',
				element:
					<StripeTaxId
						businessCountryFormControl={businessCountryFormControl}
						businessNameFormControl={businessNameFormControl}
						taxIdFormControl={taxIdFormControl}
						countryOptions={countryOptions}
						removeTaxIdErrorMessage={removeTaxIdErrorMessage}
					/>,
				buttonBack:
					<Button
						colorType="grey"
						onClick={goBack}
					>
						Back
					</Button>,
				button:
					<Button
						colorType="primary"
						onClick={submitTaxDetailsButtonClick}
						busy={createStripeCustomerLoading}
					>
						Continue
					</Button>,
				loader:
					<Progress type="wide" panelClass={classes.loaderPaper}>
						Verifying business details...
					</Progress>
			},
			{
				label: 'Billing information',
				element:
					<StripeBusinessAddress
						addressLine1FormControl={addressLine1FormControl}
						addressLine2FormControl={addressLine2FormControl}
						addressStateFormControl={addressStateFormControl}
						addressCityFormControl={addressCityFormControl}
						addressPostcodeFormControl={addressPostcodeFormControl}
						businessCountryFormControl={businessCountryFormControl}
						removeBillingAddressErrorMessage={removeBillingAddressErrorMessage}
						readonlyCountryOption={{
							label: stripeTaxCountryInfo?.name ?? '',
							value: stripeTaxCountryInfo?.code ?? ''
						}}
					/>,
				buttonBack:
					<Button
						colorType="grey"
						onClick={handleBack}
						busy={createStripeSubscriptionLoading}
					>
						Back
					</Button>,
				button:
					<Button
						colorType="primary"
						onClick={submitBillingInfoClick}
						busy={createStripeSubscriptionLoading}
					>
						Continue
					</Button>,
				loader:
					<Progress type="wide" panelClass={classes.loaderPaper}>
						Verifying business details...
					</Progress>
			},
			{
				label: 'Payment',
				element:
					stripeSubscriptionData && (
						<>
							<StripePaymentForm
								onElementsReady={onElementsReady}
								stripeSubscriptionData={stripeSubscriptionData}
							/>
							<Box my={3}>
								<TermsAndConditionsCheckbox
									acceptedTerms={acceptedTermsFormControl.value}
									setAcceptedTerms={(acceptedTerms: boolean) => acceptedTermsFormControl.setValue(acceptedTerms)}
									showError={acceptedTermsFormControl.showError}
								/>
							</Box>
						</>
					),
				buttonBack:
					<Button
						colorType="grey"
						onClick={handleBack}
						busy={paymentLoading}
					>
						Back
					</Button>
				,
				button:
					<Button
						colorType="primary"
						onClick={createStripeSubscriptionButtonClick}
						busy={paymentLoading}
					>
						Pay now and start subscription
					</Button>,
				loader:
					<Loader />
			}
		]
	};

	if (stripeTaxCountriesLoading || stripeIsoCountriesLoading) return <Loader />;
	const loading = createStripeSubscriptionLoading || createStripeCustomerLoading;

	return (
		<Grid container spacing={2}>
			{stripeError &&
				<Grid item xs={12}>
					<AlertBox
						visible={true}
						styleName="error"
						onClose={() => setStripeError('')}
					>
						{stripeError}
					</AlertBox>
				</Grid>
			}
			<Stepper activeStep={activeStep} orientation="vertical" className={classes.stepperRoot}>
				{steps().map((step) => (
					<Step key={step.label} >
						<StepLabel >
							{step.label}
						</StepLabel>
						<StepContent>
							{loading
								? <Grid container >
									<Grid item xs={12}>
										{step.loader}
									</Grid>
								</Grid>
								: <Grid container spacing={2}>
									<Grid item xs={12}>
										{step.element}
									</Grid>
									<Grid item xs={12} className={classes.buttonSpacing}>
										{step.buttonBack}
										{step.button}
									</Grid>
								</Grid>
							}
						</StepContent>
					</Step>
				))}
			</Stepper>
		</Grid>
	);
}
